import i18n from "i18next";

// Dosya yükleme üst sınırları.
//
// Video DIŞI her şey (görsel, ses, doküman, zip) için tek global sınır: 100 MB.
// Video ayrı ve daha yüksek: transkod edilecek kaynak dosyalar doğal olarak
// büyük oluyor.
//
// Backend aynası: idea-middleware'de
// IdealStudioTrainingService.MAX_VIDEO_FILE_SIZE_BYTES (video için 500 MB).
// Video sınırını değiştirirken İKİSİNİ birlikte güncelle — yalnızca burayı
// yükseltmek dosyayı boşuna yükletip sunucuda reddettirir, yalnızca orayı
// yükseltmek kullanıcıyı istemcide boşuna engeller.
//
// Not: backend'de video DIŞI tipler için sunucu tarafı boyut kontrolü YOK;
// aşağıdaki 100 MB yalnızca istemci tarafı bir nezaket sınırı.
export const MAX_UPLOAD_MB = 100;
export const MAX_UPLOAD_BYTES = MAX_UPLOAD_MB * 1024 * 1024;

export const MAX_VIDEO_UPLOAD_MB = 500;
export const MAX_VIDEO_UPLOAD_BYTES = MAX_VIDEO_UPLOAD_MB * 1024 * 1024;

// Backend'in VIDEO_EXTENSIONS listesiyle aynı (ideal-studio-training.service.ts).
// İkisi ayrışırsa istemci "geçer" deyip sunucu reddeder ya da tersi olur.
const VIDEO_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "webm",
  "avi",
  "mkv",
  "wmv",
  "flv",
  "mpg",
  "mpeg",
  "3gp",
  "ts",
  "mts",
  "m2ts",
  "vob",
  "asf",
  "rm",
  "rmvb",
  "divx",
  "ogm",
  "f4v",
  "m2v",
]);

// Dosya video mu? MIME önce, yok/yanlışsa uzantıya bakılır — backend'in
// isVideoFile() mantığının aynısı.
export function isVideoFile(file) {
  if (!file) return false;
  if (typeof file.type === "string" && file.type.startsWith("video/")) return true;
  const name = typeof file.name === "string" ? file.name : "";
  const dot = name.lastIndexOf(".");
  if (dot < 0) return false;
  return VIDEO_EXTENSIONS.has(name.slice(dot + 1).toLowerCase());
}

/** Bu dosya için geçerli üst sınır (MB). */
export function maxUploadMbFor(file) {
  return isVideoFile(file) ? MAX_VIDEO_UPLOAD_MB : MAX_UPLOAD_MB;
}

// Dosya limiti aşıyor mu? (null/geçersiz dosyalar için false döner.)
// Tip duyarlı: video için 500 MB, diğerleri için 100 MB. Tüm çağrı yerleri
// (VideoModal, görsel/ses sekmeleri, zip import, server_utils'in son savunma
// hattı) bu tek fonksiyondan geçtiği için doğru sınır kendiliğinden uygulanıyor.
export function isFileTooLarge(file) {
  if (!file || typeof file.size !== "number") return false;
  return file.size > maxUploadMbFor(file) * 1024 * 1024;
}

// Boyut aşımı için kullanıcıya gösterilecek çeviri metni.
// `file` verilirse mesajdaki sayı o dosyanın gerçek sınırını gösterir; verilmezse
// global (video dışı) sınıra düşer — eski çağrı biçimiyle uyumlu kalsın diye.
export function fileTooLargeMessage(file) {
  const max = file === undefined ? MAX_UPLOAD_MB : maxUploadMbFor(file);
  return (
    i18n.t("FileSizeLimit.TooLarge", { max }) || `File is too large. The maximum size is ${max}MB.`
  );
}

// Seçilen dosyayı doğrula: boyut aşılırsa hata metni, aksi halde "" döner.
// UI onChange/onDrop handler'larında erken uyarı için kullan.
export function validateUploadSize(file) {
  return isFileTooLarge(file) ? fileTooLargeMessage(file) : "";
}

// ── Metne Çevir (transcribe) sınırları ──────────────────────────────────────────
// Kullanılan API: screen-training/transcribe → Whisper. Whisper 25MB'lık sesi
// otomatik parçalıyor; asıl risk çok UZUN (çok chunk → yavaş/kredi) veya çok
// BÜYÜK (processor tüm videoyu belleğe indiriyor) video. Bu yüzden süre + boyut
// sınırı koyuyoruz. Backend aynası: idea-middleware screen-training transcribe
// guard (DTO @Max + service + processor Content-Length). İkisini birlikte güncelle.
export const MAX_TRANSCRIBE_DURATION_SECONDS = 60 * 60; // 60 dakika
export const MAX_TRANSCRIBE_VIDEO_MB = MAX_VIDEO_UPLOAD_MB; // 500 MB
export const MAX_TRANSCRIBE_VIDEO_BYTES = MAX_VIDEO_UPLOAD_BYTES;

export function isTranscribeDurationTooLong(durationSeconds) {
  return typeof durationSeconds === "number" && durationSeconds > MAX_TRANSCRIBE_DURATION_SECONDS;
}

export function isTranscribeVideoTooLarge(sizeBytes) {
  return typeof sizeBytes === "number" && sizeBytes > MAX_TRANSCRIBE_VIDEO_BYTES;
}

export function transcribeDurationTooLongMessage() {
  const maxMinutes = Math.round(MAX_TRANSCRIBE_DURATION_SECONDS / 60);
  return (
    i18n.t("TranscriptionVideoTooLong", { maxMinutes }) ||
    `Video is too long for transcription. The maximum is ${maxMinutes} minutes.`
  );
}

export function transcribeVideoTooLargeMessage() {
  const maxMB = MAX_TRANSCRIBE_VIDEO_MB;
  return (
    i18n.t("TranscriptionVideoTooLarge", { maxMB }) ||
    `Video is too large for transcription. The maximum is ${maxMB}MB.`
  );
}
